"use client";

import { Button, Grid, Heading, Text, TextInput } from "@afa-shared/afa-components";
import React, { useId, useRef, useState } from "react";

import FormValidationFeedback from "../Feedback/FormValidationFeedback";
import { scrollIntoView, validate } from "../formHelper";
import { trackFormStart, trackFormSubmit } from "../formTracker";
import styles from "./CalculateFPTForm.module.css";

interface ICalculateFPTForm {
  contentfulPreviewEntryId?: string;
}

const SEK_FORMATTER = new Intl.NumberFormat("sv-SE", {
  style: "currency",
  currency: "SEK",
  maximumFractionDigits: 0,
});

const formatSEK = (n: number) => SEK_FORMATTER.format(n);

export const CalculateFPTForm = ({ contentfulPreviewEntryId }: ICalculateFPTForm) => {
  const [showForm, setShowForm] = useState(true);
  const [formStarted, setFormStarted] = useState(false);

  const [hasSingleError, setHasSingleError] = useState(false);
  const [afaMonthly, setAfaMonthly] = useState(0);
  const [fkMonthly, setFkMonthly] = useState(0);

  const [formState, setFormState] = useState({
    monthlyIncome: { value: "", required: true },
    daysPerMonth: { value: "", required: true },
  });

  // Errors map: include all fields that can error/scroll
  const [errors, setErrors] = useState<Record<string, any>>({
    monthlyIncome: null,
    daysPerMonth: null,
  });

  // Refs for focusing on error fields
  const formRefs = {
    errorInfobox: useRef<HTMLDivElement | null>(null),
    monthlyIncome: useRef<HTMLInputElement | null>(null),
    daysPerMonth: useRef<HTMLInputElement | null>(null),
  };

  const formIds = {
    monthlyIncome: useId(),
    daysPerMonth: useId(),
  };

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    key: keyof typeof formState,
  ) => {
    if (!formStarted) {
      trackFormStart("föräldrapenningtillägg");
      setFormStarted(true);
    }
    const { value } = e.target;
    setFormState((prev) => ({ ...prev, [key]: { ...prev[key], value } }));
    setErrors((prev) => ({ ...prev, [key]: null }));
  };

  const onCancel = () => {
    setShowForm(true);
    setFormState({
      monthlyIncome: { value: "", required: true },
      daysPerMonth: { value: "", required: true },
    });
  };

  const calculateFPT = (monthlyIncome: number, daysPerMonth: number) => {
    const DAYS_PER_YEAR = 365;

    const annualIncome = monthlyIncome * 12;
    const fkDaily = (annualIncome * 0.97 * 0.8) / DAYS_PER_YEAR;
    const afaDaily = (annualIncome / DAYS_PER_YEAR) * 0.1;

    return {
      fkMonthly: fkDaily * daysPerMonth,
      afaMonthly: afaDaily * daysPerMonth,
    };
  };

  const onSubmit = async (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setHasSingleError(false);
    const formErrors = validate(formState as any, errors);
    setErrors(formErrors);
    const foundErrors = Object.values(formErrors).filter((v) => v !== null).length;

    if (foundErrors) {
      if (foundErrors === 1) {
        setHasSingleError(true);
        const key = Object.keys(formErrors).find((k) => formErrors[k] !== null)!;
        scrollIntoView(formRefs[key]);
      } else {
        scrollIntoView(formRefs.errorInfobox);
      }
      return;
    }

    const monthlyIncome = Number(formState.monthlyIncome.value);
    const daysPerMonth = Number(formState.daysPerMonth.value);

    const { fkMonthly, afaMonthly } = calculateFPT(monthlyIncome, daysPerMonth);

    setAfaMonthly(afaMonthly);
    setFkMonthly(fkMonthly);

    setShowForm(false);
    trackFormSubmit("föräldrapenningtillägg");
  };

  return (
    <Grid
      direction="column"
      className={styles.grid}
      data-contentful-entry-id={contentfulPreviewEntryId}
      data-contentful-field-id="name"
    >
      <div className={styles.container}>
        <div className={styles.formHeadingWrapper}>
          <Heading variant="h3" removeMargin>
            Räkna ut föräldrapenningtillägg
          </Heading>
          <Text variant="paragraph" removeMargin>
            Uträkningen baseras på en årsinkomst upp till högst 10 prisbasbelopp och den
            månadsinkomst och antal dagar du har fyllt i.
          </Text>
        </div>
        {!showForm && (
          <div className={styles.summary}>
            <div className={styles.summarySection}>
              <div className={styles.item}>
                <Text variant="span" removeMargin>
                  Månadsinkomst (före skatt)
                </Text>
                <Text variant="span" removeMargin>
                  {formatSEK(Number(formState.monthlyIncome.value))}
                </Text>
              </div>
              <div className={styles.item}>
                <Text variant="span" removeMargin>
                  Antal dagar per månad
                </Text>
                <Text variant="span" removeMargin>
                  {formState.daysPerMonth.value} dagar
                </Text>
              </div>
            </div>

            <div className={styles.summarySection}>
              <Heading variant="h3" removeMargin>
                Uppskattad ersättning
              </Heading>
              <div>
                <div className={styles.item}>
                  <Text variant="span" removeMargin>
                    Försäkringskassan
                  </Text>
                  <Text variant="span" removeMargin>
                    {formatSEK(fkMonthly)}
                  </Text>
                </div>
                <span className={styles.description}>
                  {formatSEK(fkMonthly / Number(formState.daysPerMonth.value))}/dag
                </span>
              </div>
              <div>
                <div className={styles.item}>
                  <Text variant="span" removeMargin>
                    Afa Försäkring
                  </Text>
                  <Text variant="span" removeMargin>
                    {formatSEK(afaMonthly)}
                  </Text>
                </div>
                <span className={styles.description}>
                  {formatSEK(afaMonthly / Number(formState.daysPerMonth.value))}/dag
                </span>
              </div>
              <div>
                <div className={styles.item}>
                  <Text variant="span" weight="bold" removeMargin>
                    Totalt
                  </Text>
                  <Text variant="span" weight="bold" removeMargin>
                    {formatSEK(fkMonthly + afaMonthly)}
                  </Text>
                </div>
              </div>
            </div>
            <Button
              type="submit"
              buttonType="secondary"
              onClick={onCancel}
              label="Ny beräkning"
              className={styles.submitButton}
            />
          </div>
        )}
        {showForm && (
          <>
            <div ref={formRefs.errorInfobox}>
              <FormValidationFeedback
                formErrors={errors}
                formRefs={formRefs}
                hasSingleError={hasSingleError}
              />
            </div>
            <form noValidate className={styles.form}>
              <TextInput
                inputRef={formRefs.monthlyIncome}
                label="Månadsinkomst (före skatt) *"
                description="Totalt inkomst per månad i kronor."
                id={formIds.monthlyIncome}
                name="monthlyIncome"
                value={formState.monthlyIncome.value}
                type="number"
                error={errors.monthlyIncome?.input}
                fullWidth
                required
                onChange={(e) => handleFormChange(e, "monthlyIncome")}
              />

              <TextInput
                inputRef={formRefs.daysPerMonth}
                label="Antal dagar per månad *"
                description="Dagar du planerar att ta ut eller har tagit ut föräldrapenning under månaden."
                id={formIds.daysPerMonth}
                min={1}
                max={31}
                name="daysPerMonth"
                value={formState.daysPerMonth.value}
                type="number"
                error={errors.daysPerMonth?.input}
                fullWidth
                required
                onChange={(e) => handleFormChange(e, "daysPerMonth")}
              />
              <Button
                type="submit"
                buttonType="primary"
                onClick={onSubmit}
                label="Uppskatta ersättning"
                className={styles.submitButton}
              />
            </form>
          </>
        )}
      </div>
    </Grid>
  );
};
export default CalculateFPTForm;
